/**
 * Bundles/kits & BOM editor (SPEC screen 6) — one grid over
 * `product_components`. The 422 `bom_cycle_detected` problem renders its
 * `cycle[]` extension as the friendly loop message with each SKU linked;
 * absent the extension it degrades to the problem `detail`.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Combobox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";
import { useMutation } from "@tanstack/react-query";

import { surfaceProblem } from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { formatQuantity } from "@/lib/format";

import {
  addComponent,
  removeComponent,
  type ComponentType,
  type ProductComponent,
  type ProductDetail,
  type Uom,
  type Variant,
} from "../api";
import { toQuantityString } from "../variant-schema";

type AddError =
  | { kind: "cycle"; cycle: string[]; detail?: string }
  | { kind: "duplicate" }
  | { kind: "uom"; detail?: string; from?: string; to?: string }
  | { kind: "qty" };

export interface ComponentsPanelProps {
  product: ProductDetail;
  /** The product's components (GET /products/{id}/components, position order). */
  components: ProductComponent[];
  /** All variants (component picker + SKU resolution for component rows). */
  allVariants: Variant[];
  /** All statuses, retained so legacy component rows keep their UoM code. */
  uoms: Uom[];
  /** Active-only choices; undefined until the server-filtered lookup resolves. */
  uomChoices: Uom[] | undefined;
  onChanged: () => void;
  /** Cycle-loop SKU link → that SKU's product (via the citext list filter). */
  onOpenSku: (sku: string) => void;
  /** `uom_unconvertible` deep link → UoM admin pre-filled with the pair. */
  onOpenUomPair: (fromUom: string | undefined, toUom: string | undefined) => void;
  canCreate?: boolean;
  canDelete?: boolean;
}

export function ComponentsPanel({
  product,
  components,
  allVariants,
  uoms,
  uomChoices,
  onChanged,
  onOpenSku,
  onOpenUomPair,
  canCreate = true,
  canDelete = true,
}: ComponentsPanelProps) {
  const { t, i18n } = useTranslation();
  const [componentVariantId, setComponentVariantId] = useState<string | null>(null);
  const [qty, setQty] = useState("1");
  const [uomId, setUomId] = useState(product.base_uom_id);
  const [addError, setAddError] = useState<AddError | null>(null);

  const selectedUomIsActive = uomChoices?.some((uom) => uom.id === uomId) ?? false;

  useEffect(() => {
    if (uomChoices !== undefined && uomId !== "" && !selectedUomIsActive) setUomId("");
  }, [selectedUomIsActive, uomChoices, uomId]);

  // Kind is fixed by context: bundles compose bundles, everything else is BOM.
  const componentType: ComponentType = product.product_type === "bundle" ? "bundle" : "bom";

  const skuOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const variant of allVariants) map.set(variant.id, variant.sku);
    return map;
  }, [allVariants]);
  const uomCode = (id: string): string => uoms.find((uom) => uom.id === id)?.code ?? id;

  const options = useMemo(
    () =>
      allVariants
        .filter((variant) => variant.product_id !== product.id)
        .map((variant) => ({ value: variant.id, label: variant.sku })),
    [allVariants, product.id],
  );

  const addMutation = useIdempotentMutation({
    mutationFn: (
      input: { component_variant_id: string; qty: string; uom_id: string },
      key: string,
    ) => addComponent(product.id, { ...input, component_type: componentType }, key),
    onSuccess: () => {
      setComponentVariantId(null);
      setQty("1");
      setAddError(null);
      onChanged();
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        const problem = error.problem as
          { cycle?: unknown; detail?: string; from_uom?: string; to_uom?: string } | undefined;
        if (error.code === "bom_cycle_detected") {
          const cycle = Array.isArray(problem?.cycle)
            ? problem.cycle.filter((sku): sku is string => typeof sku === "string")
            : [];
          setAddError({ kind: "cycle", cycle, detail: problem?.detail });
          return;
        }
        if (error.code === "duplicate_component") {
          setAddError({ kind: "duplicate" });
          return;
        }
        if (error.code === "uom_unconvertible") {
          setAddError({
            kind: "uom",
            detail: problem?.detail,
            from: problem?.from_uom,
            to: problem?.to_uom,
          });
          return;
        }
      }
      surfaceProblem(error, t);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (componentId: string) => removeComponent(product.id, componentId),
    onSuccess: onChanged,
    onError: (error: unknown) => surfaceProblem(error, t),
  });

  const submit = () => {
    if (componentVariantId === null || !selectedUomIsActive) return;
    // Canonicalize to the CatalogQuantityString wire scale (exactly six
    // fractional digits) by string padding — never via Number().
    const wireQty = toQuantityString(qty);
    if (wireQty === null) {
      setAddError({ kind: "qty" });
      return;
    }
    setAddError(null);
    addMutation.mutate({ component_variant_id: componentVariantId, qty: wireQty, uom_id: uomId });
  };

  const renderQty = (value: string): string => {
    try {
      return formatQuantity(value, i18n.language);
    } catch {
      return value;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {product.product_type === "bundle" && components.length === 0 ? (
        <p role="status" className="text-sm text-warning-foreground">
          {t("catalog.components.emptyBundleHint", "Empty bundles cannot be sold.")}
        </p>
      ) : null}

      <div className="w-full overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th scope="col" className="p-2 text-start font-medium">
                {t("catalog.components.component", "Component")}
              </th>
              <th scope="col" className="p-2 text-end font-medium">
                {t("catalog.components.qty", "Qty")}
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                {t("catalog.components.uom", "UoM")}
              </th>
              <th scope="col" className="p-2 text-start font-medium">
                {t("catalog.components.kind", "Kind")}
              </th>
              <th scope="col" className="p-2" />
            </tr>
          </thead>
          <tbody>
            {components.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">
                  {t("catalog.components.empty", "No components — add the first component.")}
                </td>
              </tr>
            ) : (
              components.map((component) => (
                <tr key={component.id} className="border-b">
                  <td className="p-2">
                    <span dir="ltr" className="font-mono">
                      {skuOf.get(component.component_variant_id) ?? component.component_variant_id}
                    </span>
                  </td>
                  <td className="p-2 text-end">
                    {/* physical-ok: qty digits render LTR even in RTL text */}
                    <span dir="ltr" className="tabular-nums">
                      {renderQty(component.qty)}
                    </span>
                  </td>
                  <td className="p-2">{uomCode(component.uom_id)}</td>
                  <td className="p-2">
                    <Badge variant="outline">
                      {t(
                        `catalog.components.kindLabel.${component.component_type}`,
                        component.component_type,
                      )}
                    </Badge>
                  </td>
                  <td className="p-2 text-end">
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("catalog.components.remove", "Remove component")}
                        disabled={removeMutation.isPending}
                        onClick={() => removeMutation.mutate(component.id)}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {canCreate ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="components-variant">
              {t("catalog.components.pickComponent", "Component variant")}
            </Label>
            <Combobox
              id="components-variant"
              options={options}
              value={componentVariantId}
              onChange={(next) => {
                setComponentVariantId(next);
                setAddError(null);
              }}
              placeholder={t("catalog.components.pickPlaceholder", "Search SKU…")}
              searchPlaceholder={t("catalog.components.pickSearch", "Search SKU…")}
              emptyMessage={t("catalog.components.pickEmpty", "No matching variant.")}
              className="w-56"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="components-qty">{t("catalog.components.qty", "Qty")}</Label>
            <Input
              id="components-qty"
              dir="ltr"
              inputMode="decimal"
              className="w-28"
              value={qty}
              onChange={(event) => {
                setQty(event.target.value);
                setAddError(null);
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="components-uom">{t("catalog.components.uom", "UoM")}</Label>
            <Select value={uomId} onValueChange={setUomId}>
              <SelectTrigger id="components-uom" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(uomChoices ?? []).map((uom) => (
                  <SelectItem key={uom.id} value={uom.id}>
                    {uom.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">{t("catalog.components.kind", "Kind")}</span>
            <Badge variant="outline">
              {t(`catalog.components.kindLabel.${componentType}`, componentType)}
            </Badge>
          </div>
          <Button
            type="button"
            onClick={submit}
            disabled={componentVariantId === null || !selectedUomIsActive || addMutation.isPending}
            loading={addMutation.isPending}
          >
            {t("catalog.components.add", "Add component")}
          </Button>
        </div>
      ) : null}

      {addError !== null ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {addError.kind === "cycle" ? (
            addError.cycle.length > 0 ? (
              <span className="flex flex-wrap items-center gap-1">
                <span>
                  {t("catalog.components.cycleError", "Adding {{sku}} would create a loop:", {
                    sku: addError.cycle[0] ?? "",
                  })}
                </span>
                {addError.cycle.map((sku, index) => (
                  <span key={`${sku}-${index}`} className="flex items-center gap-1">
                    {index > 0 ? (
                      <ArrowRight className="size-3 rtl:-scale-x-100" aria-hidden="true" />
                    ) : null}
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 font-mono text-sm"
                      onClick={() => onOpenSku(sku)}
                    >
                      <span dir="ltr">{sku}</span>
                    </Button>
                  </span>
                ))}
              </span>
            ) : (
              <span>
                {addError.detail ??
                  t("catalog.components.cycleFallback", "This component would create a loop.")}
              </span>
            )
          ) : addError.kind === "duplicate" ? (
            <span>
              {t("catalog.components.duplicate", "This component already exists on the product.")}
            </span>
          ) : addError.kind === "qty" ? (
            <span>
              {t(
                "catalog.components.invalidQty",
                "Enter a non-negative quantity with at most 6 decimal places.",
              )}
            </span>
          ) : (
            <span className="flex flex-wrap items-center gap-1">
              <span>
                {addError.detail ??
                  t(
                    "catalog.components.uomUnconvertible",
                    "No conversion from this unit to the product's base unit.",
                  )}
              </span>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-sm"
                onClick={() => onOpenUomPair(addError.from, addError.to)}
              >
                {t("catalog.components.addConversion", "Add the missing conversion")}
              </Button>
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}
